













































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { DivIcon, LatLng, LatLngBounds, Point } from 'leaflet';
import { LControl, LControlZoom, LMap, LImageOverlay, LMarker, LPopup, LLayerGroup } from 'vue2-leaflet';
import { namespace } from 'vuex-class';
import { mixins } from 'vue-class-component';
import CategoryIcon from '@/components/category/CategoryIcon.vue';
import ChangeLanguage from '@/mixins/ChangeLanguage';
import LocationContract from '@/location/LocationContract';
import Map from '@/mixins/Map';
import MarkerContract from '@/marker/MarkerContract';
import MarkerPopup from '@/components/marker/MarkerPopup.vue';
import ScaledMarker from '@/marker/ScaledMarker';
import MarkerIconContract from '@/marker/MarkerIconContract';
import Marker from '@/marker/Marker';

const Markers = namespace('markers');

@Component({
    components: {
        LControl,
        LControlZoom,
        LImageOverlay,
        LLayerGroup,
        LMap,
        LMarker,
        LPopup,
        MarkerPopup,
    },
})
export default class LocationMap extends mixins(ChangeLanguage, Map) {
    private widthAnimationDuration = 500;
    private promotedMarker? = '';
    private promoting = true;
    private scale = 0.5;

    @Prop({ type: Object, required: true })
    location!: LocationContract;

    @Prop({ type: Object, required: false })
    activeMarker!: MarkerContract | null;

    @Prop({ type: String, required: false })
    activeMarkerCategory!: string | null;

    @Markers.Mutation
    setActiveMarkerId!: (markerId: string | null) => void;

    mounted() {
        if (this.isMobile) {
            this.setZoom(-1);
        }

        this.checkPromotion();
    }

    public $refs!: {
        markerRef: Array<LMarker>;
        locationMap: LMap;
    };

    @Watch('$route.params.location', { deep: true })
    private onRouteChange(newValue: string, oldValue: string) {
        if (newValue !== oldValue && this.$refs.locationMap) {
            this.checkPromotion();

            if (this.isMobile) {
                this.setZoom(-1);
            }
        }
    }

    @Watch('$route.params.marker', { deep: true })
    private onMarkerChange(newValue: string, oldValue: string) {
        const marker = this.marker;
        if ((!newValue || !oldValue) && !this.isMobile && this.$refs.locationMap) {
            setTimeout(() => {
                this.$refs.locationMap.mapObject.invalidateSize({ animate: true });
                if (marker !== null) {
                    this.moveToMarker(marker);
                }
            }, this.widthAnimationDuration);
        } else if (newValue) {
            setTimeout(() => {
                if (marker !== null) {
                    this.moveToMarker(marker);
                }
            }, this.widthAnimationDuration);
        }
    }

    private moveToMarker(marker: MarkerContract): void {
        const currentZoom = this.$refs.locationMap.mapObject.getZoom();
        const zoomValue = Math.max(1, currentZoom);
        this.$refs.locationMap.mapObject.stop().setView(marker.latLng(), zoomValue, { animate: true });
    }

    @Watch('center')
    public resetZoom(newValue: LatLng, oldValue: LatLng): void {
        if (newValue.equals(oldValue, 1.0E-4) || !this.$route.params.marker) {
            return;
        }
        if (!this.isMobile) {
            this.setZoom(0);
        }
    }

    public get shouldEmbed(): boolean {
        return this.marker?.marker__type === 'embed_marker';
    }

    private get marker(): MarkerContract | null {
        const id = this.$route.params.marker;
        const marker = Marker.query()
            .whereId(id)
            .withAllRecursive()
            .first();

        return marker ? new ScaledMarker(marker, this.scale) : null;
    }

    private get isMobile(): boolean {
        return window.matchMedia('(max-width: 48rem)').matches;
    }

    private isPromotedMarker(id: string): boolean {
        return id === this.promotedMarker;
    }

    private checkPromotion(): void {
        if (this.$route.params.marker) { return; }

        const promotedMarkers = this.markers.filter(marker => marker.marker_promoted);
        if (promotedMarkers.length > 0) {
            const promotedMarker = promotedMarkers[0];
            this.promotedMarker = promotedMarker.id;
            this.promoting = true;

            if (this.isMobile) {
                const currentZoom = this.$refs.locationMap.mapObject.getZoom();
                this.$refs.locationMap.mapObject.setView([promotedMarker.latLng().lat, promotedMarker.latLng().lng], currentZoom);
            }

            setTimeout(() => {
                this.openMarkerPopup(promotedMarker);
            }, 10);
        }
    }

    private setZoom(level: number): void {
        this.$refs.locationMap.mapObject.setZoom(level);
    }

    private closeMobileLocationPanel(): void {
        if (this.isMobile) {
            this.$emit('close-location-panel');
        }
    }

    private get bounds(): LatLngBounds {
        return new LatLngBounds(
            new LatLng(0, 1024),
            new LatLng(1024, 0),
        );
    }

    private get center(): LatLng {
        if (this.markers.length === 0) {
            return this.mapCenter;
        }

        // @ts-ignore
        return new LatLngBounds(this.markers.map(marker => marker.latLng())).getCenter();
    }

    private get mapCenter(): LatLng {
        return new LatLng(512, 512);
    }

    private get image(): string {
        return this.location.map.sizes.default;
    }

    private get markers(): Array<MarkerContract> {
        return this.location.markers.map(marker => {
            return new ScaledMarker(marker, this.scale);
        });
    }

    private icon(marker: MarkerContract): DivIcon {
        const markerIcon: MarkerIconContract = {
            id: marker.id,
            activeId: marker.category.id,
            svg: marker.category.icon.sizes.default,
            color: marker.category.color.color,
            title: marker.title,
            slug: marker.slug(),
        };
        return this.buildIcon(markerIcon);
    }

    private buildIcon(markerIcon: MarkerIconContract): DivIcon {
        let size = 32;
        let svg: string | null;
        svg = markerIcon.svg;
        let className = `leaflet-marker-icon--${markerIcon.slug} leaflet-marker-icon--active`;

        if (this.activeMarkerCategory && this.activeMarkerCategory !== markerIcon.activeId) {
            size = size / 2;
            svg = null;
            className = `leaflet-marker-icon--${markerIcon.slug}`;
        }
        const route = this.$route;
        const instance = new Vue({
            render(h) {
                return h(CategoryIcon, {
                    props: {
                        color: markerIcon.color,
                        svg: svg,
                        title: markerIcon.title,
                        active: route.params.marker ? markerIcon.id === route.params.marker : false,
                    },
                });
            },
        });

        const icon = instance.$mount();

        return new DivIcon({
            className: className,
            html: icon.$el.outerHTML,
            popupAnchor: new Point(0, -size / 4),
            iconAnchor: new Point(size / 2, size / 2),
            iconSize: new Point(size, size),
        });
    }

    private openMarkerDetail(marker: MarkerContract): void {
        if (this.$route.params.marker === marker.id) {
            return;
        }

        if (this.promotedMarker) {
            this.closePromotedMarker();
        }

        this.$router.push({
            name: 'marker',
            params: {
                locale: this.currentLanguage.code,
                location: this.location.id,
                marker: marker.id,
            },
        });
    }

    private openMarkerPopup(hoveredMarker: MarkerContract): void {
        const marker = this.$refs.markerRef.filter(marker => marker.name === hoveredMarker.id)[0];
        marker.mapObject.openPopup();

        if (this.promotedMarker !== '' && !this.promoting) {
            this.closePromotedMarker();
        }
    }

    private closeMarkerPopup(hoveredMarker: MarkerContract): void {
        const marker = this.$refs.markerRef.filter(marker => marker.name === hoveredMarker.id)[0];
        marker.mapObject.closePopup();
    }

    private closePromotedMarker(): void {
        if (!this.promoting) {
            return;
        }

        const promotedMarker = this.$refs.markerRef.filter(marker => marker.name === this.promotedMarker)[0];
        this.promotedMarker = '';
        this.promoting = false;
        promotedMarker.mapObject.closePopup();
    }
}
